import { useMemo } from 'react';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getAskRovoClickedEvent } from '../../askRovoAnalytics.ts';
import { useTwgAnalyticsEvents } from '../../useTwgAnalyticsEvents.ts';

import { useShareContextLinksPrompt } from './useShareContextLinksPrompt.ts';

export type ShareContextRovoActionOptions = {
	contentId?: string;
	onAskRovo?: (prompt: string) => void;
};

export function useShareContextRovoAction({
	contentId,
	onAskRovo,
}: ShareContextRovoActionOptions): (() => void) | undefined {
	const { createAnalyticsEvent } = useTwgAnalyticsEvents();
	const { isRovoLLMEnabled } = useSessionData();
	const linksPrompt = useShareContextLinksPrompt(contentId);

	// As in useAskRovoWithLinksAction, returning no handler lets one check hide unavailable
	// Rovo and narrow its prompt. A Dev Docs-only byline contributes no URL, so null is reachable.
	return useMemo(() => {
		if (!onAskRovo || !isRovoLLMEnabled || linksPrompt === null) {
			return undefined;
		}
		return () => {
			// Tool-click events carry contextLevel, which does not control Rovo's links prompt.
			// Rovo therefore reports its own Ask Rovo event instead of a share-context tool click.
			createAnalyticsEvent(getAskRovoClickedEvent({ trigger: 'shareContext' })).fire();
			onAskRovo(linksPrompt);
		};
	}, [createAnalyticsEvent, isRovoLLMEnabled, linksPrompt, onAskRovo]);
}
